/**
 * Marketing Message → Code Ad Generator (Variation Editor).
 * Used from CampaignDetail for both editing existing variations and creating
 * new ones from a plain-English marketing message.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import CodePreview from "@/components/CodePreview";
import {
  Sparkles, RefreshCw, X, Plus, Trash2, GripVertical, Wand2,
} from "lucide-react";
import { toast } from "sonner";

const ITEM_TYPES = ["code", "comment", "terminal", "pause"];
const LANGS = ["typescript", "javascript", "bash", "python", "rust"];

function seqToCodeLines(seq) {
  // for live preview: just collapse to plain lines (no pauses)
  return (seq || []).filter((it) => it.type !== "pause").map((it) => it.text || "");
}

function makeBlank() {
  return [
    { type: "code", text: "const engineer = 1;" },
    { type: "code", text: "let output = engineer;" },
    { type: "pause", text: "", duration: 500 },
    { type: "code", text: "output *= 9;" },
    { type: "comment", text: "// 9x more effective" },
  ];
}

export default function VariationEditor({
  open, onClose, onSaved, variation, campaignId, templates,
}) {
  // Code ads only — statement templates are handled by StatementEditor.
  const codeTemplates = (templates || []).filter((t) => (t.kind || "code") === "code");
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [language, setLanguage] = useState("typescript");
  const [typeSpeed, setTypeSpeed] = useState("medium");
  const [marketingMessage, setMarketingMessage] = useState("");
  const [seq, setSeq] = useState([]);
  const [finalReveal, setFinalReveal] = useState([]);
  const [cta, setCta] = useState("");
  const [angle, setAngle] = useState("");
  const [busy, setBusy] = useState(false);

  // Initialize from variation prop when modal opens
  useEffect(() => {
    if (!open) return;
    if (variation) {
      setName(variation.name || "");
      setTemplateId(variation.template_id || codeTemplates[0]?.id || "");
      setLanguage(variation.language || "typescript");
      setTypeSpeed(variation.type_speed || "medium");
      setMarketingMessage(variation.marketing_message || "");
      // Prefer code_sequence; fall back to code_lines.
      if (variation.code_sequence && variation.code_sequence.length) {
        setSeq(variation.code_sequence.map((it) => ({ ...it })));
      } else if (variation.code_lines && variation.code_lines.length) {
        setSeq(variation.code_lines.map((t) => ({ type: "code", text: t })));
      } else {
        setSeq(makeBlank());
      }
      setFinalReveal(variation.final_reveal || []);
      setCta(variation.cta || "");
      setAngle(variation.angle || "");
    } else {
      // brand-new
      setName("New variation");
      setTemplateId(codeTemplates[0]?.id || "");
      setLanguage("typescript");
      setTypeSpeed("medium");
      setMarketingMessage("");
      setSeq(makeBlank());
      setFinalReveal([]);
      setCta("");
      setAngle("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, variation, templates]);

  if (!open) return null;
  const template = codeTemplates.find((t) => t.id === templateId) || codeTemplates[0];
  const previewLines = seqToCodeLines(seq);

  const generate = async () => {
    if (!marketingMessage.trim()) {
      toast.error("Type a marketing message first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/message-to-code", {
        message: marketingMessage,
        preferred_language: language,
      });
      setName(data.title || name);
      setLanguage(data.language || language);
      setSeq((data.code_sequence || []).map((it) => ({ ...it })));
      setFinalReveal(data.final_reveal || []);
      setCta(data.cta || "");
      setAngle(data.angle || "");
      toast.success("Generated code animation");
    } catch (e) {
      const d = e?.response?.data?.detail;
      let msg = e?.message || "AI failed";
      if (Array.isArray(d)) {
        // Pydantic 422 error array
        msg = d.map((x) => {
          const field = (x.loc || []).filter((p) => p !== "body").join(".");
          return field ? `${field}: ${x.msg}` : x.msg;
        }).join(" · ");
      } else if (typeof d === "string") {
        msg = d;
      } else if (e?.response?.status) {
        msg = `${e.response.status} · ${msg}`;
      }
      toast.error("AI failed — " + msg, { duration: 8000 });
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!templateId) { toast.error("Pick a template"); return; }
    try {
      const payload = {
        campaign_id: campaignId,
        template_id: templateId,
        name: name || "Variation",
        code_lines: previewLines,                 // keep legacy field in sync
        code_sequence: seq,
        final_reveal: finalReveal,
        marketing_message: marketingMessage,
        language, type_speed: typeSpeed,
        cta, angle,
      };
      if (variation?.id) {
        await api.patch(`/variations/${variation.id}`, payload);
      } else {
        await api.post("/variations", payload);
      }
      toast.success("Saved");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const updateItem = (idx, patch) => {
    setSeq((s) => s.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeItem = (idx) => setSeq((s) => s.filter((_, i) => i !== idx));
  const addItem = (type = "code") => {
    setSeq((s) => [...s, type === "pause" ? { type, text: "", duration: 500 } : { type, text: "" }]);
  };
  const move = (idx, dir) => {
    setSeq((s) => {
      const next = [...s];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return next;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-xl grid grid-cols-1 md:grid-cols-5 relative overflow-hidden my-4 max-h-[calc(100vh-2rem)]">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 z-10">
          <X className="h-5 w-5" />
        </button>

        {/* Preview */}
        <div className="md:col-span-2 p-6 border-r border-gray-100 bg-gray-50 space-y-4">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Preview</p>
            <CodePreview template={template} codeLines={previewLines} animate />
          </div>
          {finalReveal.length > 0 && (
            <div className="bg-gray-900 text-white rounded-lg p-4 text-center">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Final reveal</p>
              {finalReveal.map((line, i) => (
                <p key={i} className={`font-display ${i === finalReveal.length - 1 ? "text-brand" : "text-white"} ${
                  finalReveal.length === 1 ? "text-2xl" : "text-xl"
                }`}>{line}</p>
              ))}
            </div>
          )}
        </div>

        {/* Editor */}
        <div className="md:col-span-3 p-6 space-y-4 overflow-y-auto max-h-[85vh]">
          <div>
            <h2 className="font-display text-2xl text-gray-900">
              {variation?.id ? "Edit variation" : "New variation"}
            </h2>
            <p className="text-sm text-gray-500 mt-1">Type a marketing message → generate animated code → fine-tune.</p>
          </div>

          {/* Marketing message generator */}
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
            <label className="block text-xs font-semibold text-brand-700 uppercase tracking-wider mb-2">
              Marketing Message
            </label>
            <textarea
              data-testid="m2c-message"
              rows={2}
              placeholder='e.g. "Codira makes engineers 9x more effective."'
              value={marketingMessage}
              onChange={(e) => setMarketingMessage(e.target.value)}
              maxLength={4000}
              className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm bg-white"
            />
            <p className="mt-1 text-[10px] text-gray-500 tabular-nums">
              {marketingMessage.length} / 4000
            </p>
            <div className="flex items-center justify-between mt-3 gap-3">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="rounded-lg border border-gray-200 focus:border-brand focus:outline-none px-3 py-1.5 text-xs bg-white">
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <div className="flex gap-2">
                {seq.length > 0 && (
                  <button onClick={generate} disabled={busy} title="Regenerate"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors disabled:opacity-40">
                    <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Regenerate
                  </button>
                )}
                <button
                  data-testid="m2c-generate"
                  onClick={generate} disabled={busy}
                  className="btn-brand inline-flex items-center gap-1.5 px-3 py-1.5 text-xs">
                  {busy ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Generating…</>
                        : <><Wand2 className="h-3.5 w-3.5" /> Generate Code Animation</>}
                </button>
              </div>
            </div>
          </div>

          {/* Name + Template + Speed row */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Template</label>
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm bg-white">
                {codeTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type speed</label>
              <select value={typeSpeed} onChange={(e) => setTypeSpeed(e.target.value)}
                className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm bg-white">
                <option>slow</option><option>medium</option><option>fast</option>
              </select>
            </div>
          </div>

          {/* Sequence editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-gray-700">Code sequence</label>
              <div className="flex gap-1">
                {ITEM_TYPES.map((t) => (
                  <button key={t} type="button" onClick={() => addItem(t)}
                    className="text-[10px] uppercase tracking-wider rounded border border-gray-200 hover:border-brand hover:text-brand px-2 py-1 text-gray-600 transition-colors inline-flex items-center gap-1">
                    <Plus className="h-3 w-3" /> {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {seq.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 py-1.5 group hover:bg-gray-100 transition-colors">
                  <div className="flex flex-col">
                    <button onClick={() => move(idx, -1)} className="text-gray-400 hover:text-gray-900 text-[8px]">▲</button>
                    <button onClick={() => move(idx, +1)} className="text-gray-400 hover:text-gray-900 text-[8px]">▼</button>
                  </div>
                  <select value={it.type} onChange={(e) => updateItem(idx, { type: e.target.value })}
                    className="text-[10px] uppercase tracking-wider rounded border border-gray-200 bg-white px-1.5 py-1 w-24">
                    {ITEM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {it.type === "pause" ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input type="number" min={100} max={3000} step={50}
                        value={it.duration ?? 500}
                        onChange={(e) => updateItem(idx, { duration: Number(e.target.value) })}
                        className="w-24 rounded border border-gray-200 px-2 py-1 text-xs" />
                      <span className="text-xs text-gray-500">ms</span>
                    </div>
                  ) : (
                    <input value={it.text || ""}
                      onChange={(e) => updateItem(idx, { text: e.target.value })}
                      className="flex-1 font-mono text-xs rounded border border-gray-200 bg-white px-2 py-1 focus:border-brand focus:outline-none"
                      spellCheck={false} />
                  )}
                  <button onClick={() => removeItem(idx)} className="text-gray-300 hover:text-red-500">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {seq.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-6">No items — add some or generate from a message above.</p>
              )}
            </div>
          </div>

          {/* Final reveal editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-gray-700">Final reveal · 1–3 lines</label>
              <button onClick={() => setFinalReveal((r) => [...r, ""])}
                className="text-[10px] uppercase tracking-wider rounded border border-gray-200 hover:border-brand hover:text-brand px-2 py-1 text-gray-600 inline-flex items-center gap-1">
                <Plus className="h-3 w-3" /> line
              </button>
            </div>
            <div className="space-y-1.5">
              {finalReveal.map((line, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={line}
                    onChange={(e) => setFinalReveal((r) => r.map((x, k) => k === i ? e.target.value : x))}
                    placeholder={`Reveal line ${i + 1}`}
                    className="flex-1 rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm" />
                  <button onClick={() => setFinalReveal((r) => r.filter((_, k) => k !== i))}
                    className="text-gray-300 hover:text-red-500">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {finalReveal.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-3">No reveal — the code ends with a blinking cursor.</p>
              )}
            </div>
          </div>

          {/* CTA + Angle (display-only metadata) */}
          {(cta || angle) && (
            <div className="grid grid-cols-2 gap-3 text-xs text-gray-500">
              {cta && <div><span className="font-medium text-gray-700">CTA:</span> {cta}</div>}
              {angle && <div><span className="font-medium text-gray-700">Angle:</span> {angle}</div>}
            </div>
          )}

          <div className="pt-2 flex justify-end gap-2">
            <button onClick={onClose}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button data-testid="variation-save" onClick={save}
              className="btn-brand inline-flex items-center gap-2 px-5 py-2 text-sm">
              <Sparkles className="h-4 w-4" /> Save variation
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
